import WebViewer, { WebViewerInstance } from '@pdftron/webviewer'
import * as React from 'react'
import {
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	FormControl,
	IconButton,
	InputLabel,
	LinearProgress,
	MenuItem,
	Modal,
	Typography,
} from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import Select, { SelectChangeEvent } from '@mui/material/Select'
import CloseIcon from '@mui/icons-material/Close'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import { useTokenClaims } from '../../security/useTokenClaims'
import DeleteForeverIcon from '@mui/icons-material/DeleteForever'
import { AttachmentInfo, BlobAddress } from '../../types/Upload/AttachmentResponse'
import { showSuccessToast, showErrorToast } from '../../services/CommonServices/alertServices'
import { ApiResponse } from '../../services/CommonServices/httpService'
import { hasConnection } from '../../services/CommonServices/healthGate'
import { usePermissionContext } from '../../security/PermissionContext'

type AttachmentOperations = {
	listAttachments: (id: number) => Promise<AttachmentInfo[]>
	getAttachment: (attachmentId: number, id: number) => Promise<ApiResponse<unknown> | Blob | null>
	uploadAttachment: (
		files: File[],
		workitemId: number,
		overwrite: boolean,
		newAnnotations?: string,
		setUploadProgress?:
			| React.Dispatch<
					React.SetStateAction<
						| {
								fileName: string
								progress: number
						  }[]
						| undefined
					>
			  >
			| undefined,
	) => Promise<boolean>
	deleteAttachment: (attachmentId: number, id: number) => Promise<boolean>
}

type ViewAttachmentsPopupProps = {
	handleClose: () => void
	open: boolean
	itemId: number
	attachmentOperations: AttachmentOperations
}

export const ViewAttachmentsPopup = ({
	handleClose,
	open,
	itemId: id,
	attachmentOperations,
}: ViewAttachmentsPopupProps) => {
	const disabledElementsList: string[] = [
		'leftPanel',
		'leftPanelButton',
		'searchPanel',
		'searchPanelButton',
		'thumbnailsPanel',
		'thumbnailsPanelButton',
		'outlinesPanel',
		'outlinesPanelButton',
		'viewControlsButton',
	]

	const viewerRef = React.useRef<HTMLDivElement | null>()
	const originalAnnotations = useRef<string>()
	const { firstName, lastName } = useTokenClaims()
	const {hasPermission} = usePermissionContext()
	const canDeleteAttachments = hasPermission(43)
	const canUploadAttachments = hasPermission(42)
	const [attachmentsList, setAttachmentsList] = useState<AttachmentInfo[]>([])
	const [webviewerInstance, setWebviewerInstance] = useState<WebViewerInstance>()
	const [filesSelected, setFilesSelected] = useState<File[]>()
	const [openProgressModal, setOpenProgressModal] = useState(false)
	const [uploadProgress, setUploadProgress] = useState<{ fileName: string; progress: number }[]>()
	const [openDeleteDialog, setOpenDeleteDialog] = useState(false)
	const [attachmentToDelete, setAttachmentToDelete] = useState<AttachmentInfo>()
	const selectedAttachment = useRef<AttachmentInfo>()
	const fileRef = useRef<HTMLInputElement>(null)

	const [isSaving, setIsSaving] = useState(false)
	const [openSaveDialog, setOpenSaveDialog] = useState(false)

	const getAllAttachments = async () => {
		const attachments = await attachmentOperations.listAttachments(id)
		return attachments
	}

	const getBlob = async (attachmentId: number): Promise<BlobAddress | Blob> => {
		const result = await attachmentOperations.getAttachment(attachmentId, id)
		if (result instanceof Blob) {
			return result
		}
		return result?.data as BlobAddress
	}

	const saveDocument = async (instance: WebViewerInstance) => {
		setIsSaving(true)
		const { documentViewer, annotationManager } = instance.Core
		const doc = documentViewer.getDocument()
		const xfdfString = await annotationManager.exportAnnotations()
		const data = await doc.getFileData({
			// saves the document with annotations
			xfdfString,
		})
		const blob = new Blob([data], { type: 'application/pdf' })
		const file = new File([blob], selectedAttachment.current!.name , { type: 'application/pdf' })
		let newAnnotations: string | undefined = undefined
		if (originalAnnotations.current) {
			const addedOrModifiedAnnots = annotationManager
				.getAnnotationsList()
				.filter((annot) => (annot as any).IsAdded || (annot as any).IsModified)
			newAnnotations = await annotationManager.exportAnnotations({ annotList: addedOrModifiedAnnots })
		}
		
		const updateFileresult = await attachmentOperations.uploadAttachment([file], id, true, newAnnotations)
		if (updateFileresult) {
			showSuccessToast('File Saved Successfully')
			await getAsyncAttachments()
		}
		setIsSaving(false)
	}

	const getAsyncAttachments = async () => {
		const result = await getAllAttachments()
		setAttachmentsList(result)
	}

	useEffect(() => {
		getAsyncAttachments()
	}, [])

	const viewPdf = async (filePath: string | Blob) => {
		if (viewerRef.current) {
			if (webviewerInstance) {
				webviewerInstance.UI.loadDocument(filePath, {
					extension: 'pdf',
					withCredentials: false,
				})
			} else {
				WebViewer(
					{
						path: '/lib',
						disabledElements: disabledElementsList,
						streaming: true,
						fullAPI: false,
						isReadOnly: !(await hasConnection()),
						licenseKey:
							'The Brock Group, Inc. (www.brockgroup.com):ENTERP:Brock Workbook::B+:AMS(20230930):F1A591520477F80AF360B13AC9A2537920611FA9F7389D3306CD2B84BD045E37128A31F5C7',
					},
					viewerRef.current,
				).then(async (instance: WebViewerInstance) => {
					instance.UI.loadDocument(filePath, {
						withCredentials: false,
						extension: 'pdf',
					})
					setWebviewerInstance(instance)
					const { documentViewer, annotationManager } = instance.Core
					documentViewer.enableViewportRenderMode()
					documentViewer.enableLoadingAnnotationsFromVisiblePages()
					originalAnnotations.current = await annotationManager.exportAnnotations()
					annotationManager.setCurrentUser(`${firstName} ${lastName}`)
					instance.UI.setHeaderItems((header) => {
						header.push({
							type: 'actionButton',
							img: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M0 0h24v24H0z" fill="none"/><path d="M17 3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V7l-4-4zm-5 16c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3zm3-10H5V5h10v4z"/></svg>',
							onClick: async () => {
								await saveDocument(instance)
							},
						})
					})
				})
			}
		}
	}

	const onFileSelected = async (filesSelected: File[]) => {
		if (filesSelected) {
			setUploadProgress(
				filesSelected.map((f) => ({
					fileName: f.name,
					progress: 0,
				})),
			)
			setOpenProgressModal(true)
			const result = await attachmentOperations.uploadAttachment(
				filesSelected,
				id,
				false,
				undefined,
				setUploadProgress,
			)
			if (result) {
				showSuccessToast('File Uploaded Successfully')
				getAsyncAttachments()
			}
		}
	}
	useEffect(() => {
		if (!filesSelected || filesSelected.length === 0) return
		if (filesSelected.length > 5) {
			showErrorToast({ message: 'Only 5 files at a time' })
			setFilesSelected(undefined)
			return
		}
		onFileSelected(filesSelected)
	}, [filesSelected])
	const handleChange = (event: any) => {
		setFilesSelected(Array.from(event.target.files))
		event.target.value = null
	}

	const openFile = () => {
		fileRef.current!.click()
	}

	async function handleDeleteConfirm(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
		if (!attachmentToDelete) return
		const result = await attachmentOperations.deleteAttachment(attachmentToDelete?.id, id)
		if (result) {
			setOpenDeleteDialog(false)
			showSuccessToast('Attachment Deleted Successfully')
			getAsyncAttachments()
		}
	}

	// dispose WebViewer when the dialog closes to free memory
	useEffect(() => {
		if (!open && webviewerInstance) {
			webviewerInstance.UI.dispose()
			webviewerInstance.Core.documentViewer?.dispose()
			setWebviewerInstance(undefined)
			if (viewerRef.current) {
				viewerRef.current.innerHTML = ''
			}
		}
	}, [open])

	return (
		<Modal open={open} onClose={handleClose}>
			<Box
				sx={{
					position: 'absolute',
					bgcolor: 'background.paper',
					top: '50%',
					left: '50%',
					transform: 'translate(-50%, -50%)',
					boxShadow: 24,
					width: '100vw',
					height: '100vh',
				}}
			>
				<Box
					sx={{
						p: 2,
						minHeight: '100%',
						display: 'flex',
						flexDirection: 'column',
					}}
				>
					<Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
						<input
							hidden
							type='file'
							accept='application/pdf'
							ref={fileRef}
							onChange={handleChange}
							multiple
						/>
						<FormControl sx={{ display: 'flex', flexDirection: 'row' }}>
							<InputLabel>Select File</InputLabel>
							<Select
								onChange={(event: SelectChangeEvent) => {
									selectedAttachment.current = attachmentsList.find((a) => a.id === Number(event.target.value))
									getBlob(Number(event.target.value)).then((result) => {
										if (result instanceof Blob) {
											viewPdf(result)
										} else {
											viewPdf(result.uri + result.sasToken)
										}
									})
								}}
								sx={{ minWidth: '300px', maxWidth: '500px' }}
								label='Select File'
							>
								{attachmentsList.map((value, index) => (
									<MenuItem key={index} value={value.id} sx={{ justifyContent: 'space-between' }}>
										{value.name}
										{canDeleteAttachments && (
											<IconButton
												onMouseDown={(e) => {
													e.stopPropagation()
													e.preventDefault()
													setAttachmentToDelete(value)
													setOpenDeleteDialog(true)
												}}
											>
												<DeleteForeverIcon color='error' />
											</IconButton>
										)}
									</MenuItem>
								))}
								{canUploadAttachments && (
									<MenuItem
										sx={{ backgroundColor: '#cccccc', justifyContent: 'space-between' }}
										onClick={() => openFile()}
									>
										Upload Workitem Attachment <UploadFileIcon />
									</MenuItem>
								)}
							</Select>

							{isSaving && <CircularProgress color='success' sx={{ margin: 1 }} />}
						</FormControl>
						<Button
							color='error'
							variant='contained'
							sx={{ width: '4rem', height: '4rem' }}
							onClick={async (e) => {
								if (selectedAttachment.current && (await hasConnection())) {
									setOpenSaveDialog(true)
								} else {
									handleClose()
								}
							}}
						>
							<CloseIcon fontSize='large' />
						</Button>
					</Box>

					{/* this div represents the pdftron section, where pdf is rendered */}
					<Box
						sx={{
							display: 'flex',
							alignItems: 'stretch',
							flexGrow: 1,
						}}
					>
						<div
							style={{ paddingTop: '1rem', minHeight: '100%', width: '100%' }}
							ref={(el) => {
								viewerRef.current = el
							}}
						></div>
					</Box>
					{/* Save Changes Dialog */}
					<Dialog open={openSaveDialog} fullWidth onClose={() => setOpenSaveDialog(false)}>
						<DialogTitle>Save Changes</DialogTitle>
						<DialogContent>
							<DialogContentText id='alert-dialog-description'>
								Do you want to save the changes?
							</DialogContentText>
						</DialogContent>
						<DialogActions>
							<Button
								onClick={() => {
									if (!webviewerInstance) {
										handleClose()
										return
									}

									setOpenSaveDialog(false)
									saveDocument(webviewerInstance).then(() => handleClose())
								}}
								variant='contained'
								color='success'
							>
								Yes
							</Button>
							<Button onClick={handleClose} autoFocus variant='contained' color='warning'>
								No
							</Button>
						</DialogActions>
					</Dialog>

					{/* Delete Attachment Dialog */}
					<Dialog open={openDeleteDialog} fullWidth onClose={() => setOpenDeleteDialog(false)}>
						<DialogTitle>Confirm Delete</DialogTitle>
						<DialogContent>
							<DialogContentText id='alert-dialog-description'>
								Are you sure you want to delete this attachment?
							</DialogContentText>
						</DialogContent>
						<DialogActions>
							<Button onClick={handleDeleteConfirm} variant='contained' color='success'>
								Yes
							</Button>
							<Button
								onClick={() => setOpenDeleteDialog(false)}
								autoFocus
								variant='contained'
								color='warning'
							>
								No
							</Button>
						</DialogActions>
					</Dialog>
				</Box>
				{/* Upload Progress Modal */}
				<Modal open={openProgressModal} onClose={() => setOpenProgressModal(false)}>
					<Box
						sx={{
							position: 'absolute',
							bgcolor: 'background.paper',
							top: '50%',
							left: '50%',
							transform: 'translate(-50%, -50%)',
							boxShadow: 24,
							display: 'flex',
							flexDirection: 'column',
							justifyContent: 'center',
							alignItems: 'center',
							p: 2,
						}}
					>
						{uploadProgress?.map((file, index) => (
							<Box sx={{ width: '100%', marginBottom: 2 }} key={index}>
								<Typography variant='body2' sx={{ marginBottom: 1 }}>
									{file.fileName}
								</Typography>
								<LinearProgress variant='determinate' value={file.progress} />
							</Box>
						))}
						<Button
							variant='contained'
							disabled={uploadProgress?.some((upload) => upload.progress !== 100)}
							sx={{ m: 1 }}
							onClick={() => {
								setOpenProgressModal(false)
							}}
						>
							Done
						</Button>
					</Box>
				</Modal>
			</Box>
		</Modal>
	)
}
